/**
 * App router: protected routes, mode dispatchers, and runtime role-group catch-all.
 */

import * as React from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import { RootLayout } from '@/components/layout/RootLayout';
import { RbacLoader } from '@/components/layout/RbacLoader';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { CalendarPage } from '@/components/pages/CalendarPage';
import { DashboardPage } from '@/components/dashboard/DashboardPage';
import { SessionsRouteDispatcher } from '@/app/dispatchers/SessionsRouteDispatcher';
import { EventsAndSummariesManagementPage } from '@/components/pages/management-pages/EventsAndSummariesManagementPage';
import { TutorsRouteDispatcher } from '@/app/dispatchers/TutorsRouteDispatcher';
import { GamificationLeaderboardPage } from '@/components/pages/GamificationLeaderboardPage';
import { StudentsRouteDispatcher } from '@/app/dispatchers/StudentsRouteDispatcher';
import { ParentsManagementPage } from '@/components/pages/management-pages/ParentsManagementPage';
import { VolunteersManagementPage } from '@/components/pages/management-pages/VolunteersManagementPage';
import { AdminsManagementPage } from '@/components/pages/management-pages/AdminsManagementPage';
import { EstablishmentsManagementPage } from '@/components/pages/management-pages/EstablishmentsManagementPage';
import { ReportsRouteDispatcher } from '@/app/dispatchers/ReportsRouteDispatcher';
import { VscReportsManagementPage } from '@/components/pages/management-pages/VscReportsManagementPage';
import { AssessmentsManagementPage } from '@/components/pages/management-pages/AssessmentsManagementPage';
import { SubjectsManagementPage } from '@/components/pages/management-pages/SubjectsManagementPage';
import { LevelsManagementPage } from '@/components/pages/management-pages/LevelsManagementPage';
import { ProgramsManagementPage } from '@/components/pages/management-pages/ProgramsManagementPage';
import { SchoolYearsManagementPage } from '@/components/pages/management-pages/SchoolYearsManagementPage';
import { ActivityTypesManagementPage } from '@/components/pages/management-pages/ActivityTypesManagementPage';
import { ActivitiesManagementPage } from '@/components/pages/management-pages/ActivitiesManagementPage';
import { HolidaysManagementPage } from '@/components/pages/management-pages/HolidaysManagementPage';
import { PairsRouteDispatcher } from '@/app/dispatchers/PairsRouteDispatcher';
import { SpecialtiesManagementPage } from '@/components/pages/management-pages/SpecialtiesManagementPage';
import { VisioBackgroundsManagementPage } from '@/components/pages/management-pages/VisioBackgroundsManagementPage';
import { RolesManagementPage } from '@/components/pages/management-pages/RolesManagementPage';
import { TutorialsPage } from '@/components/pages/TutorialsPage';
import { LoginPage } from '@/components/pages/LoginPage';
import { UnauthorizedPage } from '@/components/pages/UnauthorizedPage';
import { NotFoundPage } from '@/components/pages/NotFoundPage';
import { useAuth } from '@/hooks/useAuth';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { DynamicRoleGroupManagementPage } from '@/components/common/DynamicRoleGroupManagementPage';
import { PartnersManagementPage } from '@/components/pages/management-pages/PartnersManagementPage';
import { SportClubsManagementPage } from '@/components/pages/management-pages/SportClubsManagementPage';

/** `/` → dashboard when signed in, otherwise login. */
const RootRedirect = () => {
  const { isAuthenticated } = useAuth();
  return <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />;
};

/** Tutorials page when the sidebar menu item is granted. */
const TutorialsRoute = () => {
  const { elements, isLoading } = useInterfaceElements('sidebar');
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-full">Chargement...</div>;
  }
  const allowed = elements.some((el) => el.elementId === 'menu-tutorials');
  if (!allowed) return <Navigate to="/unauthorized" replace />;
  return <TutorialsPage />;
};

/** Catch-all for runtime role groups. URL: /role-group/:groupCode (e.g. steering). */
const RoleGroupRoutePage = () => {
  const { groupCode } = useParams<{ groupCode: string }>();
  if (!groupCode) return <Navigate to="/dashboard" replace />;

  return (
    <ProtectedRoute requiredPermission={{ resource: groupCode, action: 'read' }}>
      <DynamicRoleGroupManagementPage
        interfaceName={groupCode}
        menuId={`menu-${groupCode}`}
        title={groupCode.charAt(0).toUpperCase() + groupCode.slice(1).replaceAll('-', ' ')}
        userQueryKey={groupCode}
      />
    </ProtectedRoute>
  );
};

/** Old /dashboard/<page> bookmarks and emails → /<page>. /dashboard itself stays the home. */
const LegacyDashboardPageRedirect = () => {
  const { '*': rest } = useParams();
  const location = useLocation();
  if (!rest) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Navigate to={`/${rest}${location.search}${location.hash}`} replace />;
};

/** Route table: public login, RBAC-protected pages, mode dispatchers, role-group catch-all. */
const router = createBrowserRouter([
  {
    path: '/',
    element: <RootRedirect />,
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    element: (
      <RbacLoader>
        <RootLayout />
      </RbacLoader>
    ),
    children: [
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'dashboard', action: 'read' }}>
            <DashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'calendar',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'dashboard', action: 'read' }}>
            <CalendarPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tutorials',
        element: (
          <ProtectedRoute>
            <TutorialsRoute />
          </ProtectedRoute>
        ),
      },
      {
        path: 'sessions',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'sessions', action: 'read' }}>
            <SessionsRouteDispatcher />
          </ProtectedRoute>
        ),
      },
      {
        path: 'pairs',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'pairs', action: 'read' }}>
            <PairsRouteDispatcher />
          </ProtectedRoute>
        ),
      },
      {
        path: 'events',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'events', action: 'read' }}>
            <EventsAndSummariesManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'tutors',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'tutors', action: 'read' }}>
            <TutorsRouteDispatcher />
          </ProtectedRoute>
        ),
      },
      {
        path: 'leaderboard',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'tutors', action: 'read' }}>
            <GamificationLeaderboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'students',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'students', action: 'read' }}>
            <StudentsRouteDispatcher />
          </ProtectedRoute>
        ),
      },
      {
        path: 'parents',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'parents', action: 'read' }}>
            <ParentsManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'volunteers',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'volunteers', action: 'read' }}>
            <VolunteersManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'partners',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'partners', action: 'read' }}>
            <PartnersManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'sport-clubs',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'sport-clubs', action: 'read' }}>
            <SportClubsManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admins',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'admins', action: 'read' }}>
            <AdminsManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'establishments',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'establishments', action: 'read' }}>
            <EstablishmentsManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reports',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'reports', action: 'read' }}>
            <ReportsRouteDispatcher />
          </ProtectedRoute>
        ),
      },
      {
        path: 'vscReports',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'vscReports', action: 'read' }}>
            <VscReportsManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'assessments',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'assessments', action: 'read' }}>
            <AssessmentsManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'subjects',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'subjects', action: 'read' }}>
            <SubjectsManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'levels',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'levels', action: 'read' }}>
            <LevelsManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'programs',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'programs', action: 'read' }}>
            <ProgramsManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'schoolYears',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'schoolYears', action: 'read' }}>
            <SchoolYearsManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'activityTypes',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'activityTypes', action: 'read' }}>
            <ActivityTypesManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'activities',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'activities', action: 'read' }}>
            <ActivitiesManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'holidays',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'holidays', action: 'read' }}>
            <HolidaysManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'specialties',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'specialties', action: 'read' }}>
            <SpecialtiesManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'visio-backgrounds',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'visio-backgrounds', action: 'read' }}>
            <VisioBackgroundsManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'roles',
        element: (
          <ProtectedRoute requiredPermission={{ resource: 'roles', action: 'read' }}>
            <RolesManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'unauthorized',
        element: <UnauthorizedPage />,
      },
      /**
       * Dynamic route for admin-type role groups created at runtime.
       * Sidebar link points to /role-group/steering.
       */
      {
        path: 'role-group/:groupCode',
        element: <RoleGroupRoutePage />,
      },
      {
        path: 'dashboard/*',
        element: <LegacyDashboardPageRedirect />,
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);

/** App router: public auth routes plus RBAC-gated dashboard pages. */
export const Router: React.FC = () => {
  return <RouterProvider router={router} />;
};
